/**
 * Azure Application Insights Plugin
 * Story: 116-123 - Instrument API and error telemetry
 *
 * Initializes Application Insights SDK on client-side for event tracking.
 * Connection string configured via NUXT_PUBLIC_APPINSIGHTS_CONNECTION_STRING.
 *
 * Enhancements (116-123):
 *  - PII sanitizer telemetry initializer (allowlist-based, OI-116-05)
 *  - Sampling policy: Request/Dependency 20%, Exception/Event/client_error 100%
 *  - Sev1 override: always sampled in
 *  - Correlation ID propagation via X-Correlation-ID header
 *  - Client error tracking (window.onerror, unhandledrejection)
 *  - Async dispatch is non-blocking; outage degrades gracefully
 */

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app';
import {
  emitAnalyticsOperationalSignal,
  getAnalyticsConsentBlockedReason,
  hasAnalyticsConsent,
  subscribeAnalyticsConsentChanges,
} from '../../composables/useAnalyticsConsent';
import { sanitizeTelemetryProperties } from '../../lib/telemetry/piiSanitizer';
import { shouldSampleIn, hashCorrelationId } from '../../lib/telemetry/samplingPolicy';
import { generateCorrelationId } from '../../lib/telemetry/correlationId';
import { getCurrentTimestamp } from '../../types/analytics';

/** Event name constant for client-side error telemetry (SC-116-123). */
const CLIENT_ERROR_EVENT_NAME = 'client_error' as const;

export default defineNuxtPlugin({
  name: 'appinsights',
  parallel: true,
  setup(nuxtApp) {
    const config = useRuntimeConfig();
    const connectionString = typeof config.public.appInsightsConnectionString === 'string'
      ? config.public.appInsightsConnectionString
      : '';
    let appInsights: ApplicationInsights | null = null;
    let telemetryEnabled = false;
    let isInitializing = false;
    let consentRevoked = false;
    let lastBlockedReason: string | null = null;

    function emitBlocked(reason: string): void {
      if (lastBlockedReason === reason) return;
      lastBlockedReason = reason;
      emitAnalyticsOperationalSignal({
        code: 'analytics_blocked',
        channel: 'appinsights',
        reason,
      });
    }

    function trackOperationalEvent(code: string, reason?: string): void {
      if (!telemetryEnabled || !appInsights) return;
      appInsights.trackEvent({
        name: code,
        properties: {
          surface: 'analytics-plugin',
          action: 'init',
          intent: 'ops',
          reason: reason || 'none',
        },
      });
    }

    const sessionCorrelationId = generateCorrelationId();
    const sessionSampleValue = hashCorrelationId(sessionCorrelationId);

    function scheduleInitialization(): void {
      if (typeof globalThis.window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          void initializeAppInsights();
        }, { timeout: 2000 });
        return;
      }

      void initializeAppInsights();
    }

    const onWindowError = (event: ErrorEvent): void => {
      if (!telemetryEnabled || !appInsights) return;
      appInsights.trackEvent({
        name: CLIENT_ERROR_EVENT_NAME,
        properties: {
          event_name: CLIENT_ERROR_EVENT_NAME,
          error_type: event.error?.name ?? 'Error',
          ...(event.error && 'code' in event.error && event.error.code !== undefined
            ? { error_code: String(event.error.code) }
            : {}),
          severity: 'Sev2',
          component: event.filename ?? 'unknown',
          correlation_id: sessionCorrelationId,
          timestamp: getCurrentTimestamp(),
        },
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
      if (!telemetryEnabled || !appInsights) return;
      const reason = event.reason as Error | undefined;
      const reasonRecord = reason as Record<string, unknown> | undefined;
      appInsights.trackEvent({
        name: CLIENT_ERROR_EVENT_NAME,
        properties: {
          event_name: CLIENT_ERROR_EVENT_NAME,
          error_type: reason?.name ?? 'UnhandledRejection',
          ...(typeof reasonRecord?.['code'] !== 'undefined'
            ? { error_code: String(reasonRecord['code']) }
            : {}),
          severity: 'Sev2',
          component: 'promise',
          correlation_id: sessionCorrelationId,
          timestamp: getCurrentTimestamp(),
        },
      });
    };

    async function initializeAppInsights(): Promise<void> {
      if (isInitializing || telemetryEnabled || nuxtApp._appInsightsInitialized) return;

      if (!connectionString) {
        emitBlocked('missing_appinsights_connection_string');
        return;
      }

      if (consentRevoked) {
        emitBlocked('consent_revoked');
        return;
      }

      if (!hasAnalyticsConsent()) {
        emitBlocked(getAnalyticsConsentBlockedReason());
        return;
      }

      isInitializing = true;
      try {
        const client = new ApplicationInsights({
          config: {
            connectionString,
            enableAutoRouteTracking: true,
            enableCorsCorrelation: true,
            enableRequestHeaderTracking: true,
            enableResponseHeaderTracking: true,
            disableFetchTracking: false,
            enableAjaxPerfTracking: true,
            correlationHeaderExcludedDomains: [],
          },
        });

        client.addTelemetryInitializer((envelope) => {
          const baseData = envelope.baseData as Record<string, unknown> | undefined;
          if (!baseData) return;
          const existing = baseData['properties'] as Record<string, unknown> | undefined;
          if (existing) {
            if (!existing['correlation_id']) {
              existing['correlation_id'] = sessionCorrelationId;
            }
          } else {
            baseData['properties'] = { correlation_id: sessionCorrelationId };
          }
        });

        client.addTelemetryInitializer((envelope) => {
          const baseData = envelope.baseData as Record<string, unknown> | undefined;
          if (baseData?.['properties'] && typeof baseData['properties'] === 'object') {
            baseData['properties'] = sanitizeTelemetryProperties(
              baseData['properties'] as Record<string, unknown>
            );
          }
        });

        client.addTelemetryInitializer((envelope) => {
          const itemType = envelope.baseType ?? 'EventData';
          const baseData = envelope.baseData as Record<string, unknown> | undefined;
          const props = baseData?.['properties'] as Record<string, unknown> | undefined;
          const isSev1 = props?.['severity'] === 'Sev1';
          const isClientError = baseData?.['name'] === CLIENT_ERROR_EVENT_NAME;

          if (!shouldSampleIn(itemType, isSev1, isClientError, sessionSampleValue)) {
            return false;
          }
        });

        client.loadAppInsights();
        appInsights = client;
        window.addEventListener('error', onWindowError);
        window.addEventListener('unhandledrejection', onUnhandledRejection);
        client.trackPageView();

        telemetryEnabled = true;
        nuxtApp._appInsightsInitialized = true;
        lastBlockedReason = null;
        emitAnalyticsOperationalSignal({
          code: 'analytics_initialized',
          channel: 'appinsights',
        });
        trackOperationalEvent('analytics_initialized');
      } catch (error) {
        telemetryEnabled = false;
        const reason = error instanceof Error ? error.message : 'unknown_error';
        emitAnalyticsOperationalSignal({
          code: 'analytics_init_failed',
          channel: 'appinsights',
          reason,
        });
      } finally {
        isInitializing = false;
      }
    }

    if (hasAnalyticsConsent()) {
      scheduleInitialization();
    } else {
      emitBlocked(getAnalyticsConsentBlockedReason());
    }

    const unsubscribe = subscribeAnalyticsConsentChanges((granted) => {
      if (granted) {
        emitAnalyticsOperationalSignal({
          code: 'consent_granted',
          channel: 'appinsights',
        });
        scheduleInitialization();
        return;
      }

      consentRevoked = true;
      telemetryEnabled = false;
      emitAnalyticsOperationalSignal({
        code: 'consent_denied',
        channel: 'appinsights',
        reason: 'consent_revoked',
      });
      emitBlocked('consent_revoked');
    });

    const gatedClient = {
      trackEvent: (event: { name: string; properties?: Record<string, unknown> }) => {
        if (!telemetryEnabled || !appInsights) return;
        appInsights.trackEvent(event);
      },
    };

    return {
      provide: {
        appInsights: gatedClient,
        // Helper for tracking custom events directly
        trackEvent: (name: string, properties?: Record<string, unknown>) => {
          if (!telemetryEnabled || !appInsights) return;
          appInsights.trackEvent({ name, properties });
        },
      },
    };
  },
});
